import React, { memo } from "react";
import {
  Modal,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { Colors } from "../../configs";
import locales_es from "../../locales/es";
import ButtonLinear from "../Buttons/ButtonLinear";
import Layout from "../Layout/Layout";
import Text from "../Text";

export interface ModalPatientExitConfirmProps {
  visible: boolean;
  type: "confirmSave" | "missingPhone";
  coverageName?: string;
  onSave?: () => void;
  onDiscard: () => void;
  onCancel?: () => void;
  onFix?: () => void;
  loading?: boolean;
}

const ModalPatientExitConfirm = memo((props: ModalPatientExitConfirmProps) => {
  const {
    visible,
    type,
    coverageName = "",
    onSave,
    onDiscard,
    onCancel,
    onFix,
    loading = false,
  } = props;

  if (!visible) return null;

  const isMissingPhone = type === "missingPhone";

  const title = isMissingPhone
    ? locales_es.patientEditMissingPhone.title
    : locales_es.patientEditExitConfirm.title;

  const subtitle = isMissingPhone
    ? (typeof locales_es.patientEditMissingPhone.subtitle === "function"
        ? locales_es.patientEditMissingPhone.subtitle(coverageName)
        : `La cobertura ${coverageName} requiere un teléfono de contacto. No se puede guardar hasta completarlo.`)
    : locales_es.patientEditExitConfirm.subtitle;

  return (
    <Modal
      visible={visible}
      onRequestClose={isMissingPhone ? onFix : onCancel}
      transparent
      animationType="fade"
    >
      <TouchableOpacity
        style={styles.containerModal}
        activeOpacity={1}
        onPress={isMissingPhone ? onFix : onCancel}
      >
        <TouchableWithoutFeedback>
          <Layout style={styles.card}>
            <Text
              size={18}
              lineHeight={22}
              bold
              center
              color={Colors.DarkJungleGreen || "#1B242C"}
            >
              {title}
            </Text>

            <Text
              size={14}
              lineHeight={20}
              marginTop={12}
              center
              color={Colors.Neutral900 || "#4A5568"}
            >
              {subtitle}
            </Text>

            <View style={styles.actionsContainer}>
              {isMissingPhone ? (
                <>
                  <ButtonLinear
                    testID="btnModalFixPhone"
                    white
                    title={locales_es.patientEditMissingPhone.fix}
                    onPress={onFix}
                    style={styles.primaryBtn}
                    disabled={loading}
                  />
                  <TouchableOpacity
                    testID="btnModalDiscardPhone"
                    style={styles.secondaryBtn}
                    onPress={onDiscard}
                    disabled={loading}
                    activeOpacity={0.7}
                  >
                    <Text size={15} bold color={Colors.Tomato || "#E53E3E"} center>
                      {locales_es.patientEditMissingPhone.discard}
                    </Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <ButtonLinear
                    testID="btnModalSaveExit"
                    white
                    title={locales_es.patientEditExitConfirm.save}
                    onPress={onSave}
                    style={styles.primaryBtn}
                    disabled={loading}
                  />
                  <TouchableOpacity
                    testID="btnModalDiscardExit"
                    style={styles.secondaryBtn}
                    onPress={onDiscard}
                    disabled={loading}
                    activeOpacity={0.7}
                  >
                    <Text size={15} bold color={Colors.Tomato || "#E53E3E"} center>
                      {locales_es.patientEditExitConfirm.discard}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID="btnModalCancelExit"
                    style={styles.cancelBtn}
                    onPress={onCancel}
                    disabled={loading}
                    activeOpacity={0.7}
                  >
                    <Text size={14} semibold color={Colors.GrayBlue || "#718096"} center>
                      {locales_es.patientEditExitConfirm.cancel}
                    </Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          </Layout>
        </TouchableWithoutFeedback>
      </TouchableOpacity>
    </Modal>
  );
});

export default ModalPatientExitConfirm;

const styles = StyleSheet.create({
  containerModal: {
    backgroundColor: Colors.Black68 || "rgba(0,0,0,0.68)",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
  },
  card: {
    borderRadius: 16,
    paddingHorizontal: 28,
    paddingVertical: 32,
    maxWidth: 440,
    width: "100%",
    backgroundColor: "#FFFFFF",
  },
  actionsContainer: {
    marginTop: 24,
    width: "100%",
  },
  primaryBtn: {
    width: "100%",
  },
  secondaryBtn: {
    marginTop: 14,
    paddingVertical: 10,
    alignItems: "center",
  },
  cancelBtn: {
    marginTop: 6,
    paddingVertical: 8,
    alignItems: "center",
  },
});
